"use client";

import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";

import classNames from "classnames";
import styles from "./styles/SidebarContainer.module.scss";

import FontAwesome from "../../utilities/FontAwesome";
import LoginButtons from "./items/login/LoginButtons";
import dynamic from "next/dynamic";
import { FetchMachine } from "../../machines/FetchMachine";
import { useMachine } from "@xstate/react";
import SidebarContainer from "./SidebarContainer";
import Button from "../../utilities/Button";
import { useStores } from "../../../stores/useStores";
import { ThemeProvider } from "../../radix/ThemeProvider";

const SidebarComponent: Record<string, any> = {
  pagesettings: dynamic(() => import("./pages/SidebarNavPage")),
  gridsettings: dynamic(() => import("./pages/SidebarGridSettingsPage")),
  Social: dynamic(() => import("./pages/SidebarSocialPage")),
  appearance: dynamic(() => import("./pages/SidebarAppearancePage")),
  AddSocial: dynamic(() => import("./pages/AddNewSourcePage")),
  EditSource: dynamic(() => import("./pages/EditSourcePage")),
  ErrorPage: dynamic(() => import("./pages/ErrorPage")),
  SchoolFeedPost: dynamic(() => import("./pages/SchoolFeedPostPage")),
  PublishDeletePage: dynamic(() => import("./pages/PublishDeletePage")),
  LanguagesPage: dynamic(() => import("./pages/LanguagesPage")),
  AutoApprovalsPage: dynamic(() => import("./pages/AutoApprovalsPage")),
  WelcomeEmail: dynamic(() => import("./pages/WelcomeEmailPage")),
};

const DisplayText: Record<string, string> = {
  gridsettings: "Grid Options",
  Social: "Third Party Content",
  Design: "Design",
  pagesettings: "Page Settings",
  AddSocial: "Add Content Feed",
  EditSource: "Feed Status Report",
  EditLocation: "Back",
  SchoolFeedPost: "Back",
};

const topNavButtonClassName = classNames({
  [styles.topNavButton]: true,
});
const noAccessContainerClassName = classNames({
  [styles.noAccessContainer]: true,
});
const sidebarHeaderClassName = classNames({
  [styles.sidebarHeader]: true,
});

const AdminSidebar = observer(() => {
  const { userStore, sidebarStore, interfaceStore } = useStores();

  const [currentComponent, setCurrentComponent] = useState<string | null>("");
  const [current, send] = useMachine(FetchMachine);
  const containerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (sidebarStore.sidebar) {
      const view = sidebarStore.sidebar.view;
      setCurrentComponent(view);

      if (containerRef?.current) {
        containerRef.current.scrollTop = 0;
      }
    } else if (currentComponent) {
      setCurrentComponent(null);
    }
  }, [sidebarStore.sidebar]);

  useEffect(() => {
    const contentContainer = document.querySelector("#sb-content-canvas");
    const modalContainer = document.querySelector("#allModalContainer");

    async function handleClick(e: Event) {
      if (
        currentComponent &&
        !modalContainer?.contains(e.target as Node | null)
      ) {
        sidebarStore.close();
      }
    }

    if (contentContainer) {
      contentContainer.addEventListener("click", handleClick);
    }
    return () => {
      if (contentContainer) {
        contentContainer.removeEventListener("click", handleClick);
      }
    };
  }, []);

  const ComponentToDisplay = currentComponent
    ? SidebarComponent[currentComponent]
    : () => <div />;

  return (
    <SidebarContainer ref={containerRef} in={!!currentComponent}>
      <ThemeProvider theme={{ portalContainer: containerRef }}>
        <div className={sidebarHeaderClassName}>
          {sidebarStore.historyStack.length > 1 && (
            <Button
              className={topNavButtonClassName}
              onClick={sidebarStore.back}
              title={"Back"}
            >
              <FontAwesome
                ariaHidden={true}
                prefix={"fas"}
                name="fa-chevron-left"
              />
              <h3>
                {
                  DisplayText[
                    sidebarStore.historyStack[
                      sidebarStore.historyStack.length - 2
                    ]?.view
                  ]
                }
              </h3>
            </Button>
          )}
        </div>
        {userStore.isEditor || interfaceStore.view === "schoolfeed" ? (
          <ComponentToDisplay
            currentSourceLoadingState={current}
            sendSourceLoadingState={send}
          />
        ) : (
          <div className={noAccessContainerClassName}>
            <p style={{ textAlign: "center" }}>
              Please login below to access admin panel
            </p>
            <LoginButtons />
          </div>
        )}
      </ThemeProvider>
    </SidebarContainer>
  );
});

export default AdminSidebar;
